/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, ReactElement } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import uuid from 'uuid';
import SearchUtils from '../../../utils/SearchUtils';
import { setFilteredSearchDropdownItems, setTempSearchQuery } from '../../../actions/searchActions';
import SearchDropdownHistoryItem from './SearchDropdownHistoryItem';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
type OwnProps = {
	searchHostContext: HostContext;
	logger: Logger;
	onSubmit: (...args: Array<any>) => any;
	searchQuery: string;
	hostShowSearchDropdown: boolean;
};

interface State {
	areChildrenFocusable: boolean;
}

type SearchDropdownHistoryProps = OwnProps & PropsFromRedux & WrappedComponentProps;
/*
 * SearchHistory for Search.
 */
class SearchHistory extends Component<SearchDropdownHistoryProps, State> {
	/**
	 * Returns the render for the Search History Items
	 * @returns <element>
	 */
	historyContainerRef = React.createRef<HTMLDivElement>();

	constructor(props) {
		super(props);
		this.state = {
			areChildrenFocusable: false,
		};
		this.onDelete = this.onDelete.bind(this);
	}

	componentDidUpdate(prevProps: Readonly<SearchDropdownHistoryProps>, prevState: Readonly<State>): void {
		if (this.state.areChildrenFocusable !== prevState.areChildrenFocusable) {
			if (this.state.areChildrenFocusable) {
				const firstFocusable = document.getElementById('search-dropdown-history-item-0');
				if (firstFocusable) {
					firstFocusable.focus();
					this.props.setTempSearchQuery(firstFocusable.dataset?.ccsQuery);
				}
			} else {
				this.historyContainerRef.current.focus();
			}
		}
	}

	renderHistoryTitle() {
		const historyTitleId = 'app.search.presearch.history.title';
		if (this.props.searchQuery) {
			return null;
		}
		return (
			<h5 className='search-dropdown-history-title'>
				<FormattedMessage id={historyTitleId} />
			</h5>
		);
	}

	onDelete(focusOnHisotyrDropDown: boolean) {
		if (focusOnHisotyrDropDown) {
			this.setState({ areChildrenFocusable: false });
		} else {
			const focusableItem = document.getElementById('ccsearch-input');
			focusableItem.focus();
		}
	}

	renderHistoryItems(historyItems) {
		let elements: ReactElement[] = [];
		// Return only the top 6 items
		const list = historyItems.slice(0, 6);
		SearchAnalytics.dropDownData.searchHistory = list;

		// Show the Search History Dropdown with the list of search history items
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.postSuggestionRender(list, 'suggestion', 'history');
		elements = list.map((item, index) => (
			<SearchDropdownHistoryItem
				key={uuid.v4()}
				item={item}
				index={index}
				searchQuery={this.props.searchQuery}
				hostShowSearchDropdown={this.props.hostShowSearchDropdown}
				onSubmit={this.props.onSubmit}
				onDelete={this.onDelete}
				logger={this.props.logger}
				searchHostContext={this.props.searchHostContext}
				tabIndex={this.state.areChildrenFocusable}
			/>
		));
		return elements.length ? elements : null;
	}

	getSearchDropdownItemNumber(searchDropdownNodes, searchDropdownItem) {
		let searchDropdownItemNumber = 0;

		if (searchDropdownNodes) {
			for (let i = 0; i < searchDropdownNodes.length; i++) {
				const searchDropdownItemCloseId = `${searchDropdownNodes[i].id}-close`;
				if (
					searchDropdownNodes[i].id === searchDropdownItem.id ||
					searchDropdownItemCloseId === searchDropdownItem.id
				) {
					searchDropdownItemNumber = i;
				}
			}
		}
		return searchDropdownItemNumber;
	}

	makeChildrenFocusable = () => {
		this.setState({ areChildrenFocusable: true });
	};

	makeChildrenBlurred = () => {
		this.setState({ areChildrenFocusable: false });
	};

	keyDownHandler = (event: Record<string, any>) => {
		const key = event.key;
		const keyCode = event.keyCode;
		const searchHistoryDropdownListDom = document.querySelector('.search-dropdown-history-container');
		const dropDownItems = searchHistoryDropdownListDom.querySelectorAll(
			'.search-dropdown-history-item-text-container',
		);
		const list = dropDownItems ? dropDownItems : [];
		if (key === 'Enter' || keyCode === 13) {
			this.makeChildrenFocusable();
		} else if (key === 'Escape' || keyCode === 27) {
			if (this.state.areChildrenFocusable) {
				event.stopPropagation();
				event.preventDefault();
				this.makeChildrenBlurred();
				this.props.setTempSearchQuery('');
			}
		} else if ((key === 'ArrowDown' || keyCode === 40) && this.state.areChildrenFocusable) {
			event.stopPropagation();
			event.preventDefault();
			const itemNumber = this.getSearchDropdownItemNumber(list, document.activeElement);
			const currentElement = document.activeElement;
			if (list && typeof itemNumber === 'number' && itemNumber !== list.length - 1) {
				// @ts-ignore
				currentElement.blur();
				// @ts-ignore
				list[itemNumber + 1].focus();
				// Set temporary search query
				this.props.setTempSearchQuery(list[itemNumber + 1]?.dataset?.ccsQuery);
			}
		} else if ((key === 'ArrowUp' || keyCode === 38) && this.state.areChildrenFocusable) {
			event.stopPropagation();
			event.preventDefault();
			const itemNumber = this.getSearchDropdownItemNumber(list, document.activeElement);
			const currentElement = document.activeElement;
			if (list && typeof itemNumber === 'number' && itemNumber !== 0) {
				// @ts-ignore
				currentElement.blur();
				// @ts-ignore
				list[itemNumber - 1].focus();
				// Set temporary search query
				this.props.setTempSearchQuery(list[itemNumber - 1]?.dataset?.ccsQuery);
			}
		} else if ((key === 'ArrowLeft' || keyCode === 37) && this.state.areChildrenFocusable) {
			event.stopPropagation();
			event.preventDefault();
			const currentElement = document.activeElement;
			if (currentElement.id.includes('close')) {
				const itemNumber = this.getSearchDropdownItemNumber(list, currentElement);
				// @ts-ignore
				currentElement.blur();
				// @ts-ignore
				list[itemNumber].focus();
				// Set temporary search query
				this.props.setTempSearchQuery(list[itemNumber]?.dataset?.ccsQuery);
			}
		} else if ((key === 'ArrowRight' || keyCode === 39) && this.state.areChildrenFocusable) {
			event.stopPropagation();
			event.preventDefault();
			const currentElement = document.activeElement;
			if (!currentElement.id.includes('close')) {
				const closeEltId = `${document.activeElement.id}-close`;
				const closeWithinCurrElt = searchHistoryDropdownListDom.querySelector(`#${closeEltId}`);
				if (closeWithinCurrElt instanceof HTMLElement) {
					// @ts-ignore
					// currentElement.blur();
					// @ts-ignore
					closeWithinCurrElt.focus();
				}
			}
		}
	};

	render() {
		const historyItems = SearchUtils.getFilteredSearchHistoryItems(
			this.props.searchQuery,
			this.props.searchHistoryItems,
		);
		const historyTitleId = 'app.search.presearch.history.title';
		if (historyItems && Array.isArray(historyItems) && historyItems.length > 0) {
			return (
				<>
					<div id='search-dropdown-history-container-desc' className='sr-only'>
						{`${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.enter',
						})} ${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.grid',
						})} ${this.props.intl.formatMessage({
							id: 'app.search.presearch.navigation.exit',
						})}`}
					</div>
					<div
						className='search-dropdown-history-container'
						role='option'
						id='search-dropdown-history-container'
						tabIndex={this.state.areChildrenFocusable ? undefined : -1}
						onKeyDown={this.keyDownHandler}
						ref={this.historyContainerRef}
						aria-describedby='search-dropdown-history-container-desc'
						aria-label={this.props.intl.formatMessage({
							id: historyTitleId,
						})}
					>
						{this.renderHistoryTitle()}
						<div role='grid'>{this.renderHistoryItems(historyItems)}</div>
					</div>
				</>
			);
		}
		return null;
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchHistoryItems: search.searchHistoryItems,
});

const mapDispatchToProps = {
	setFilteredSearchDropdownItems,
	setTempSearchQuery,
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchHistory));
